import React, { useState, useEffect } from 'react';
import ArchV2Hasil from './ArchV2Hasil';
import { senaraiSejarah, bacaRun, usd, usd2, saat, masa, STATUS_RUN, unjuranKos, CALL_SEHARI } from '../services/promptArchV2Service';

// ═══════════════════════════════════════════════════════════
//  PROMPT ARCHITECTURE V2 — tab Test History
//  Sejarah run makmal ini SAHAJA (koleksi `prompt_arch_v2_runs`, server sahaja) — berasingan
//  daripada sejarah generate pelanggan. Buka semula satu run, atau tanda DUA run untuk
//  dibanding sebelah-menyebelah (cth produk + prompt sama, model berbeza).
//  Setiap run menyimpan snapshot prompt yang dihantar + versi setiap komponen, jadi hasil lama
//  kekal boleh dibaca walaupun pustaka sudah disunting.
// ═══════════════════════════════════════════════════════════

const purataNilai = (nilai) => {
  const v = Object.values(nilai || {}).filter((x) => Number.isFinite(x));
  return v.length ? `${(v.reduce((a, b) => a + b, 0) / v.length).toFixed(1)} (${v.length})` : '—';
};
const susunan = (r) => `${r.nama?.kategori || '—'} · ${r.nama?.preset || `${r.nama?.cerita || '—'} + ${r.nama?.gaya || '—'}`}`;

function PromptDihantar({ run }) {
  return (
    <details className="rounded-xl bg-white border border-slate-200 p-4 mt-3">
      <summary className="text-[13px] font-semibold text-slate-800 cursor-pointer">Prompt yang dihantar untuk run ini (snapshot kekal)</summary>
      <p className="text-[11.5px] text-slate-400 m-0 mt-2 mb-1">Instructions (system)</p>
      <pre className="text-[11.5px] text-slate-600 bg-slate-50 rounded-lg p-3 whitespace-pre-wrap break-words m-0 max-h-80 overflow-y-auto">{run.prompt?.system || '(tiada)'}</pre>
      <p className="text-[11.5px] text-slate-400 m-0 mt-3 mb-1">Input (user) — termasuk snapshot data produk</p>
      <pre className="text-[11.5px] text-slate-600 bg-slate-50 rounded-lg p-3 whitespace-pre-wrap break-words m-0 max-h-80 overflow-y-auto">{run.prompt?.user || '(tiada)'}</pre>
    </details>
  );
}

function JadualBanding({ a, b }) {
  const promptSama = a.prompt?.system === b.prompt?.system && a.prompt?.user === b.prompt?.user;
  const kunci = (c) => `${c.jenis}:${c.id}`;
  const versiA = new Map((a.komponen || []).map((c) => [kunci(c), c]));
  const bezaKomponen = (b.komponen || []).filter((c) => {
    const x = versiA.get(kunci(c));
    return !x || x.versi !== c.versi;
  });
  const bil = (r) => `${Array.isArray(r.output?.scenes) ? r.output.scenes.length : 0} / ${r.pilihan?.bilBabak ?? '?'}`;
  const baris = [
    ['Masa', masa(a.createdMs), masa(b.createdMs)],
    ['Model', a.model, b.model],
    ['Reasoning · verbosity', `${a.reasoning} · ${a.verbosity || '—'}`, `${b.reasoning} · ${b.verbosity || '—'}`],
    ['Status', (STATUS_RUN[a.status] || STATUS_RUN.gagal).teks, (STATUS_RUN[b.status] || STATUS_RUN.gagal).teks],
    ['Scene (dapat / diminta)', bil(a), bil(b)],
    ['Token in', a.adaUsage ? Number(a.token?.input || 0).toLocaleString() : '—', b.adaUsage ? Number(b.token?.input || 0).toLocaleString() : '—'],
    ['Token out', a.adaUsage ? Number(a.token?.output || 0).toLocaleString() : '—', b.adaUsage ? Number(b.token?.output || 0).toLocaleString() : '—'],
    ['Latency', saat(a.latencyMs), saat(b.latencyMs)],
    ['Vision (gambar dihantar)', a.vision ? 'ON' : 'OFF', b.vision ? 'ON' : 'OFF'],
    ['Kos 1 call', a.adaUsage ? usd(a.kosUSD) : '—', b.adaUsage ? usd(b.kosUSD) : '—'],
    [`Unjuran ${CALL_SEHARI.toLocaleString()} call/hari`, a.adaUsage ? usd2(unjuranKos(a.kosUSD)?.sehari) : '—', b.adaUsage ? usd2(unjuranKos(b.kosUSD)?.sehari) : '—'],
    ['Structured mode', a.modStruktur || '—', b.modStruktur || '—'],
    ['Produk', a.namaProduk, b.namaProduk],
    ['Susunan kreatif', susunan(a), susunan(b)],
    ['Markah manual (purata)', purataNilai(a.nilai), purataNilai(b.nilai)],
  ];
  return (
    <div className="rounded-xl bg-white border border-slate-200 p-4 mb-3 overflow-x-auto">
      <p className={`text-[12.5px] font-semibold m-0 mb-2 ${promptSama ? 'text-emerald-700' : 'text-amber-700'}`}>
        {promptSama
          ? 'Prompt + data produk yang dihantar adalah SAMA — beza hasil datang daripada model/setting sahaja.'
          : 'Prompt yang dihantar BERBEZA antara dua run ini — beza hasil bukan daripada model sahaja.'}
        {!promptSama && bezaKomponen.length > 0 && ` Komponen berbeza: ${bezaKomponen.map((c) => c.label).join(', ')}.`}
      </p>
      <table className="w-full text-[12.5px]">
        <thead>
          <tr className="text-left text-slate-400 border-b border-slate-100">
            <th className="py-1.5 pr-3 font-semibold w-[200px]"></th>
            <th className="py-1.5 pr-3 font-semibold">Run A</th>
            <th className="py-1.5 font-semibold">Run B</th>
          </tr>
        </thead>
        <tbody>
          {baris.map(([label, x, y]) => (
            <tr key={label} className="border-b border-slate-50 text-slate-600">
              <td className="py-1.5 pr-3 text-slate-400">{label}</td>
              <td className={`py-1.5 pr-3 ${x !== y ? 'font-semibold text-slate-800' : ''}`}>{x}</td>
              <td className={`py-1.5 ${x !== y ? 'font-semibold text-slate-800' : ''}`}>{y}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="text-[11.5px] text-slate-400 m-0 mt-2">Kualiti (hook, arahan visual, VO, pengulangan) tidak diberi skor automatik — baca dua lajur di bawah dan isi Manual rating pada setiap run.</p>
    </div>
  );
}

export default function ArchV2Sejarah({ lib, segar, onGunaSemula }) {
  const [senarai, setSenarai] = useState([]);
  const [sedang, setSedang] = useState(true);
  const [ralat, setRalat] = useState(null);
  const [kira, setKira] = useState(0);
  const [pilih, setPilih] = useState([]); // maksimum 2 id
  const [paparan, setPaparan] = useState(null); // { jenis: 'satu' | 'banding', sedang, run?, a?, b?, ralat? }

  useEffect(() => {
    let batal = false;
    (async () => {
      try {
        const r = await senaraiSejarah(40);
        if (!batal) { setSenarai(r.senarai || []); setRalat(null); }
      } catch (e) {
        if (!batal) setRalat(e?.message || String(e));
      } finally {
        if (!batal) setSedang(false);
      }
    })();
    return () => { batal = true; };
  }, [segar, kira]);

  const muatSemula = () => { setSedang(true); setKira((n) => n + 1); };
  const togol = (id) => setPilih((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id].slice(-2)));

  const buka = async (id) => {
    setPaparan({ jenis: 'satu', sedang: true });
    try {
      setPaparan({ jenis: 'satu', sedang: false, run: await bacaRun(id) });
    } catch (e) {
      setPaparan({ jenis: 'satu', sedang: false, ralat: e?.message || String(e) });
    }
  };
  const banding = async () => {
    if (pilih.length !== 2) return;
    setPaparan({ jenis: 'banding', sedang: true });
    try {
      // Susun ikut masa: A = run lebih awal.
      const [x, y] = await Promise.all(pilih.map((id) => bacaRun(id)));
      const [a, b] = (x.createdMs || 0) <= (y.createdMs || 0) ? [x, y] : [y, x];
      setPaparan({ jenis: 'banding', sedang: false, a, b });
    } catch (e) {
      setPaparan({ jenis: 'banding', sedang: false, ralat: e?.message || String(e) });
    }
  };
  const nilaiDisimpan = (id, nilai, catatan) => {
    setSenarai((s) => s.map((r) => (r.id === id ? { ...r, nilai } : r)));
    setPaparan((p) => {
      if (!p) return p;
      const kemas = (r) => (r && r.id === id ? { ...r, nilai, catatan } : r);
      return { ...p, run: kemas(p.run), a: kemas(p.a), b: kemas(p.b) };
    });
  };

  // ── Paparan satu run / perbandingan ──
  if (paparan) {
    return (
      <div>
        <button onClick={() => setPaparan(null)} className="mb-3 text-[13px] font-semibold text-indigo-600 hover:underline">← Kembali ke senarai</button>
        {paparan.sedang && (
          <div className="rounded-xl bg-white border border-slate-200 p-8 text-center">
            <span className="material-symbols-outlined text-[28px] text-indigo-500 animate-spin">progressive_activity</span>
            <p className="text-[13px] text-slate-500 m-0 mt-2">Memuatkan run…</p>
          </div>
        )}
        {paparan.ralat && (
          <div className="rounded-xl bg-rose-50 border border-rose-200 p-4 text-[13px] text-rose-700"><span className="font-bold">Gagal membuka run:</span> {paparan.ralat}</div>
        )}
        {paparan.jenis === 'satu' && paparan.run && (
          <>
            <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
              <p className="text-[13px] text-slate-500 m-0">{masa(paparan.run.createdMs)} · <b className="text-slate-700">{paparan.run.namaProduk}</b> · {susunan(paparan.run)} · {paparan.run.pilihan?.bilBabak} scene × {paparan.run.pilihan?.tempoh}s</p>
              <button onClick={() => onGunaSemula(paparan.run)} className="h-8 px-3 text-[12px] font-semibold text-slate-600 border border-slate-200 bg-white rounded-lg hover:bg-slate-50">Guna setting ini di Playground</button>
            </div>
            <ArchV2Hasil run={paparan.run} kriteria={lib.kriteria} onNilaiDisimpan={nilaiDisimpan} />
            <PromptDihantar run={paparan.run} />
          </>
        )}
        {paparan.jenis === 'banding' && paparan.a && paparan.b && (
          <>
            <JadualBanding a={paparan.a} b={paparan.b} />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 items-start">
              <div>
                <p className="text-[12px] font-bold uppercase tracking-wide text-slate-400 m-0 mb-1.5">Run A</p>
                <ArchV2Hasil run={paparan.a} kriteria={lib.kriteria} onNilaiDisimpan={nilaiDisimpan} />
                <PromptDihantar run={paparan.a} />
              </div>
              <div>
                <p className="text-[12px] font-bold uppercase tracking-wide text-slate-400 m-0 mb-1.5">Run B</p>
                <ArchV2Hasil run={paparan.b} kriteria={lib.kriteria} onNilaiDisimpan={nilaiDisimpan} />
                <PromptDihantar run={paparan.b} />
              </div>
            </div>
          </>
        )}
      </div>
    );
  }

  // ── Senarai ──
  return (
    <div className="rounded-xl bg-white border border-slate-200 p-5">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div>
          <p className="text-[14px] font-semibold text-slate-800 m-0">Test History</p>
          <p className="text-[12px] text-slate-500 m-0 mt-0.5">40 run terbaru makmal ini. Tanda dua run → Compare. Berasingan daripada sejarah generate pelanggan.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={banding} disabled={pilih.length !== 2} className="h-9 flex items-center gap-2 px-4 text-[13px] font-bold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed">
            <span className="material-symbols-outlined text-[16px]">compare_arrows</span>Compare{pilih.length ? ` (${pilih.length}/2)` : ''}
          </button>
          <button onClick={muatSemula} disabled={sedang} className="h-9 px-3 text-[13px] font-semibold text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-40">{sedang ? 'Memuat…' : 'Refresh'}</button>
        </div>
      </div>

      {ralat && <div className="rounded-lg bg-rose-50 border border-rose-200 px-3 py-2.5 text-[12.5px] text-rose-700 mb-3"><span className="font-bold">Sejarah gagal dimuat:</span> {ralat}</div>}
      {!ralat && !sedang && senarai.length === 0 && <p className="text-[13px] text-slate-500 m-0 py-6 text-center">Belum ada run. Jana satu di tab Playground.</p>}

      {senarai.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-[12.5px]">
            <thead>
              <tr className="text-left text-slate-400 border-b border-slate-100">
                <th className="py-2 pr-2 font-semibold"></th>
                <th className="py-2 pr-3 font-semibold">Masa</th>
                <th className="py-2 pr-3 font-semibold">Status</th>
                <th className="py-2 pr-3 font-semibold">Model</th>
                <th className="py-2 pr-3 font-semibold">Produk</th>
                <th className="py-2 pr-3 font-semibold">Susunan</th>
                <th className="py-2 pr-3 font-semibold">Scene</th>
                <th className="py-2 pr-3 font-semibold">Token in / out</th>
                <th className="py-2 pr-3 font-semibold">Latency</th>
                <th className="py-2 pr-3 font-semibold">Kos</th>
                <th className="py-2 pr-3 font-semibold">Markah</th>
                <th className="py-2 font-semibold"></th>
              </tr>
            </thead>
            <tbody>
              {senarai.map((r) => {
                const st = STATUS_RUN[r.status] || STATUS_RUN.gagal;
                return (
                  <tr key={r.id} className="border-b border-slate-50 text-slate-600 hover:bg-indigo-50/30">
                    <td className="py-2 pr-2"><input type="checkbox" checked={pilih.includes(r.id)} onChange={() => togol(r.id)} aria-label="Pilih untuk banding" /></td>
                    <td className="py-2 pr-3 text-slate-400 whitespace-nowrap">{masa(r.createdMs)}</td>
                    <td className="py-2 pr-3"><span className={`text-[10.5px] font-bold px-1.5 py-0.5 rounded border whitespace-nowrap ${st.kelas}`} title={r.ralat || ''}>{st.teks}</span></td>
                    <td className="py-2 pr-3 whitespace-nowrap font-semibold text-slate-700">{r.model} <span className="font-normal text-slate-400">({r.reasoning})</span>{r.vision && <span className="ml-1 text-[10px] font-bold text-indigo-600 bg-indigo-50 px-1 py-0.5 rounded">VISION</span>}</td>
                    <td className="py-2 pr-3"><span className="block truncate max-w-[170px]">{r.namaProduk}</span></td>
                    <td className="py-2 pr-3"><span className="block truncate max-w-[230px]">{susunan(r)}</span></td>
                    <td className="py-2 pr-3 whitespace-nowrap">{r.pilihan?.bilBabak} × {r.pilihan?.tempoh}s</td>
                    <td className="py-2 pr-3 whitespace-nowrap">{Number(r.token?.input || 0).toLocaleString()} / {Number(r.token?.output || 0).toLocaleString()}</td>
                    <td className="py-2 pr-3 whitespace-nowrap">{saat(r.latencyMs)}</td>
                    <td className="py-2 pr-3 whitespace-nowrap">{usd(r.kosUSD)}</td>
                    <td className="py-2 pr-3 whitespace-nowrap">{purataNilai(r.nilai)}</td>
                    <td className="py-2 whitespace-nowrap"><button onClick={() => buka(r.id)} className="text-indigo-600 font-semibold hover:underline">buka →</button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
